import { useCallback, useMemo } from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { WintSalaryEnums } from "models/enums";
import { SalaryDeviationFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { IconDefinitionConstants } from "utils/constants";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { DeviationEmployee } from "models/propTypes/wintSalaryPropTypes";
import { WintSuperkollAPIModelWintSalaryDeviations as WintSalaryDeviationsApi } from "wint-api-client/dist/types";
//#endregion
import { DateHelper, EnumHelper, LocalizationHelperInstance } from "utils/helpers";

const apiCategoryToLocalType = (
  category: WintSalaryDeviationsApi.SalaryDeviationTypeCategory
): WintSalaryEnums.SalaryDeviationType | null => {
  switch (category) {
    case WintSalaryDeviationsApi.SalaryDeviationTypeCategory.Absence:
      return WintSalaryEnums.SalaryDeviationType.Absence;
    case WintSalaryDeviationsApi.SalaryDeviationTypeCategory.WorkedHours:
      return WintSalaryEnums.SalaryDeviationType.WorkingHours;
    case WintSalaryDeviationsApi.SalaryDeviationTypeCategory.Compensations:
      return WintSalaryEnums.SalaryDeviationType.Compensation;
    case WintSalaryDeviationsApi.SalaryDeviationTypeCategory.TimeBalances:
      return WintSalaryEnums.SalaryDeviationType.TimeBalances;
    default:
      return null;
  }
};

function useSalaryDeviationValues(employee?: DeviationEmployee, deviationTypes?: AutocompleteListItem[]) {
  const { name, personId } = useSelector((state: AppState) => state.auth.loggedInUser);

  const initialCreateValues = useMemo((): SalaryDeviationFormTypes.WintDeviationCreate => {
    return {
      employee: {
        id: employee !== undefined ? employee.id.toString() : personId.toString(),
        value: employee !== undefined ? employee.name : name,
        icon: IconDefinitionConstants.WUser,
      },
      comment: undefined,
      useCurrentMonthPlacement: false,
      timeDeviation: {
        reportType: WintSalaryEnums.SalaryDeviationReportType.FullDay,
        fillDayHours: false,
        dayHours: [],
      },
      amount: {
        // Deliberately unset — see AmountCreate.yearMonth.
        yearMonth: undefined,
        sumDescriptions: [],
      },
      travelCompensation: {
        fromTime: "08:00",
        toTime: "17:00",
        countryCode: undefined,
      },
      timeBalances: {
        yearMonth: DateHelper.getFirstDayOfPreviousMonth(new Date()),
        value: undefined,
      },
      withdrawal: {
        yearMonth: DateHelper.getFirstDayOfMonth(new Date()),
        amount: undefined,
      },
      fuelBenefit: {},
    };
  }, [name, personId, employee]);

  const getResetedValues = useCallback((values: SalaryDeviationFormTypes.WintDeviationCreate) => {
    return {
      ...values,
      deviation: undefined,
      child: undefined,
      comment: undefined,
      // Eligibility is a property of the chosen type, so a tick made on an eligible type must not
      // survive a switch to an ineligible one — the control would be hidden while the flag still
      // submitted, and the salary service would reject the whole create.
      useCurrentMonthPlacement: false,
      timeDeviation: {
        ...values.timeDeviation,
        reportType: WintSalaryEnums.SalaryDeviationReportType.FullDay,
        dateRange: undefined,
        fillDayHours: false,
        dayHours: [],
        time: undefined,
        percentage: undefined,
      },
      amount: {
        ...values.amount,
        // Deliberately unset — see AmountCreate.yearMonth.
        yearMonth: undefined,
        sumDescriptions: [],
      },
      travelCompensation: {
        ...values.travelCompensation,
        mealDeductions: undefined,
        fromDate: undefined,
        toDate: undefined,
        fromTime: "08:00",
        toTime: "17:00",
        countryCode: undefined,
      },
      timeBalances: {
        ...values.timeBalances,
        yearMonth: DateHelper.getFirstDayOfPreviousMonth(new Date()),
        value: undefined,
      },
      withdrawal: {
        yearMonth: DateHelper.getFirstDayOfMonth(new Date()),
        amount: undefined,
      },
      fuelBenefit: {},
    };
  }, []);

  const allDeviationCategories: SelectionItem[] = useMemo(() => {
    return EnumHelper.getNumericItemValues(WintSalaryEnums.SalaryDeviationType).map((type) => ({
      id: type,
      value: LocalizationHelperInstance.getTranslatedEnumValue("SalaryDeviation.DeviationType", type),
    }));
  }, []);

  const availableDeviationCategories: SelectionItem[] = useMemo(() => {
    if (!deviationTypes || deviationTypes.length === 0) {
      return [];
    }

    const uniqueCategories = new Set<WintSalaryEnums.SalaryDeviationType>();

    deviationTypes.forEach((type) => {
      const deviationType = type.data as WintSalaryDeviationsApi.SalaryDeviationType;
      if (deviationType?.hidden) {
        return;
      }

      const apiCategory = deviationType?.category;
      const localType = apiCategoryToLocalType(apiCategory);
      if (localType !== null) {
        uniqueCategories.add(localType);
      }
    });

    return Array.from(uniqueCategories)
      .sort((a, b) => a - b)
      .map((type) => ({
        id: type,
        value: LocalizationHelperInstance.getTranslatedEnumValue("SalaryDeviation.DeviationType", type),
      }));
  }, [deviationTypes]);

  const availableDeviationReportTypes: SelectionItem[] = useMemo(() => {
    return EnumHelper.getNumericItemValues(WintSalaryEnums.SalaryDeviationReportType).map((type) => ({
      id: type,
      value: LocalizationHelperInstance.getTranslatedEnumValue("SalaryDeviation.DeviationReportType", type),
    }));
  }, []);

  return {
    initialCreateValues,
    getResetedValues,
    allDeviationCategories,
    availableDeviationCategories,
    availableDeviationReportTypes,
  };
}

export default useSalaryDeviationValues;
